import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Calendar, X, Save, Loader } from 'lucide-react';
import config from '../utils/config';

export default function RachnayeAdmin() {
  const [poems, setPoems] = useState([]);
  const [form, setForm] = useState({ 
    title: '', 
    description: '', 
    author: '',
    date: '',
    image_url: '' 
  });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPoems();
  }, []);

  const fetchPoems = async () => {
    try {
      const response = await fetch(`${config.url}/rachnaye`);
      const data = await response.json();
      setPoems(data);
    } catch (error) {
      setError('Failed to fetch poems');
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const clearForm = () => {
    setForm({ 
      title: '', 
      description: '', 
      author: '',
      date: '',
      image_url: '' 
    });
    setEditing(null);
    setImageFile(null);
    setImagePreview('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('description', form.description.trim());
      formData.append('author', form.author.trim());
      formData.append('date', form.date);
      if (imageFile) {
        formData.append('image', imageFile);
      } else if (form.image_url && editing) {
        formData.append('image_url', form.image_url);
      }
      const token = localStorage.getItem('adminToken');
      const url = editing 
        ? `${config.url}/rachnaye/${editing}`
        : `${config.url}/rachnaye`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(editing ? 'रचना अपडेट हो गई!' : 'रचना सफलतापूर्वक जोड़ी गई!');
        clearForm();
        setShowForm(false);
        fetchPoems();
      } else {
        setError(data.error || 'Operation failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = item => {
    setForm({ 
      title: item.title || '', 
      description: item.description || '', 
      author: item.author || '',
      date: item.date || '',
      image_url: item.image_url || ''
    });
    setEditing(item.id);
    setImagePreview(item.image_url ? `${config.url}${item.image_url}` : '');
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async id => {
    if (!window.confirm('क्या आप वाकई इस रचना को हटाना चाहते हैं?')) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/rachnaye/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setSuccess('रचना हटा दी गई!');
        fetchPoems();
      } else {
        setError('Failed to delete');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-10 px-2 md:px-8 bg-gradient-to-br from-orange-50 to-yellow-50 min-h-screen">
      <div className="container mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-orange-700">रचनाएँ (कविता/साहित्य) प्रबंधन</h2>
          <button onClick={() => { clearForm(); setShowForm(true); }} className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded shadow transition-all">
            <Plus className="w-5 h-5" /> नई रचना
          </button>
        </div>
        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">{error}</div>}
        {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded mb-4">{success}</div>}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold mb-1">शीर्षक</label>
                <input type="text" name="title" value={form.title} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">लेखक</label>
                <input type="text" name="author" value={form.author} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">तारीख</label>
                <input type="date" name="date" value={form.date} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">छवि</label>
                <input type="file" accept="image/*" onChange={handleImageChange} className="w-full" />
                {imagePreview && <img src={imagePreview} alt="Preview" className="mt-2 w-32 h-24 object-cover rounded" />}
              </div>
              <div className="md:col-span-2">
                <label className="block font-semibold mb-1">विवरण</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="w-full border rounded px-3 py-2" rows={3} required />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button type="submit" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow transition-all" disabled={loading}>
                <Save className="w-5 h-5" /> {editing ? 'अपडेट करें' : 'जोड़ें'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded shadow transition-all">
                <X className="w-5 h-5" /> रद्द करें
              </button>
            </div>
          </form>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">शीर्षक</th>
                <th className="py-2 px-4 border-b">लेखक</th>
                <th className="py-2 px-4 border-b">तारीख</th>
                <th className="py-2 px-4 border-b">छवि</th>
                <th className="py-2 px-4 border-b">कार्रवाई</th>
              </tr>
            </thead>
            <tbody>
              {poems.map(poem => (
                <tr key={poem.id}>
                  <td className="py-2 px-4 border-b font-semibold">{poem.title}</td>
                  <td className="py-2 px-4 border-b">{poem.author}</td>
                  <td className="py-2 px-4 border-b">{poem.date ? new Date(poem.date).toLocaleDateString('hi-IN') : ''}</td>
                  <td className="py-2 px-4 border-b">
                    {poem.image_url && <img src={`${config.url}${poem.image_url}`} alt="img" className="w-16 h-12 object-cover rounded" />}
                  </td>
                  <td className="py-2 px-4 border-b">
                    <button onClick={() => handleEdit(poem)} className="text-blue-600 hover:text-blue-800 mr-2"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(poem.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="flex justify-center mt-4"><Loader className="animate-spin w-8 h-8 text-orange-600" /></div>}
      </div>
    </section>
  );
}